import { useRef, useState } from "react";
import { Check, Copy } from "lucide-react";
import type { PaperItem } from "./opsPaperworkTypes";
import { OpsPaperworkAttachmentStrip } from "./OpsPaperworkAttachmentStrip";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { isPlantPacketItem } from "@/lib/ops/plantPacket";
import type { OpsPaperworkAttachment } from "@/lib/opsPaperworkAttachmentsClient";

const tabBtn = (active: boolean) =>
  `rounded-md px-3 py-1.5 text-sm font-medium ${
    active
      ? "bg-slate-800 text-white dark:bg-zinc-200 dark:text-zinc-900"
      : "border border-gray-300 text-gray-800 hover:bg-gray-50 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800"
  }`;

type PacketLayer = "plant" | "ops";

function EvidenceUrlField(props: { value: string; onCommit: (next: string) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);

  async function copy() {
    const text = (inputRef.current?.value ?? props.value).trim();
    if (!text) {
      inputRef.current?.focus();
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      inputRef.current?.select();
    }
  }

  return (
    <div className="flex min-w-0 items-center gap-1">
      <input
        ref={inputRef}
        className="min-w-0 flex-1 truncate rounded border px-2 py-1 font-mono text-xs dark:border-zinc-700 dark:bg-zinc-900"
        placeholder="Evidence / Drive URL"
        defaultValue={props.value}
        title={props.value || "Evidence / Drive URL"}
        onBlur={(e) => {
          if (e.target.value !== props.value) props.onCommit(e.target.value);
        }}
      />
      <button
        type="button"
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-zinc-600 dark:text-zinc-300 dark:hover:bg-zinc-800"
        title="Copy URL"
        aria-label="Copy URL"
        onClick={() => void copy()}
      >
        {copied ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
      </button>
    </div>
  );
}

function PacketSlotCard(props: {
  row: PaperItem;
  kind: PacketLayer;
  attachments: OpsPaperworkAttachment[];
  dealId: string;
  canEditAttachments: boolean;
  onSave: (row: PaperItem, patch: Record<string, unknown>) => Promise<void>;
  onReload: () => Promise<void>;
}) {
  const plant = props.kind === "plant";
  return (
    <div
      className={`min-w-0 space-y-1.5 rounded-lg border p-2 text-sm ${
        plant
          ? "border-indigo-200 bg-indigo-50/40 dark:border-indigo-900 dark:bg-indigo-950/20"
          : "border-gray-200 dark:border-zinc-700"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 truncate font-medium" title={props.row.label}>
          {props.row.label}
          {props.row.required === false ? (
            <span className="ml-1 font-normal text-gray-500">optional</span>
          ) : plant ? (
            <span className="ml-1 font-normal text-amber-800 dark:text-amber-200">required</span>
          ) : null}
        </span>
        <select
          className="shrink-0 rounded border px-1 text-xs dark:border-zinc-700 dark:bg-zinc-900"
          value={props.row.status}
          onChange={(e) => void props.onSave(props.row, { status: e.target.value })}
        >
          {["missing", "waived", "received", "verified"].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      <EvidenceUrlField
        value={props.row.evidence_url ?? ""}
        onCommit={(next) => void props.onSave(props.row, { evidence_url: next })}
      />
      {plant && props.row.notes ? (
        <p className="truncate text-xs text-gray-700 dark:text-zinc-300" title={props.row.notes}>
          {props.row.notes}
        </p>
      ) : null}
      {!plant ? (
        <input
          type="datetime-local"
          className="w-full rounded border px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-900"
          defaultValue={props.row.due_at ? props.row.due_at.slice(0, 16) : ""}
          onBlur={(e) => {
            const raw = e.target.value.trim();
            void props.onSave(props.row, { due_at: raw ? new Date(raw).toISOString() : null });
          }}
        />
      ) : null}
      {props.canEditAttachments || props.attachments.length > 0 ? (
        <OpsPaperworkAttachmentStrip
          dealId={props.dealId}
          paperworkItemId={props.row.id}
          attachments={props.attachments}
          canEdit={props.canEditAttachments}
          onChanged={props.onReload}
        />
      ) : null}
    </div>
  );
}

export function OpsDealPacketSlider(props: {
  dealId: string;
  items: PaperItem[];
  attachmentsByItem: Map<string, OpsPaperworkAttachment[]>;
  canEditAttachments: boolean;
  onSavePaperRow: (row: PaperItem, patch: Record<string, unknown>) => Promise<void>;
  onReloadDetail: () => Promise<void>;
}) {
  const [layer, setLayer] = useState<PacketLayer>("plant");
  const plantItems = props.items.filter((row) => isPlantPacketItem(row));
  const opsItems = props.items.filter((row) => !isPlantPacketItem(row));
  const shown = layer === "plant" ? plantItems : opsItems;

  return (
    <section {...{ [FIREFLY_TARGET_ATTR]: "ops-deal-checklist" }}>
      <div className="mb-2 flex flex-wrap gap-2" role="tablist" aria-label="Deal paperwork packet">
        <button
          type="button"
          role="tab"
          aria-selected={layer === "plant"}
          className={tabBtn(layer === "plant")}
          onClick={() => setLayer("plant")}
        >
          Plant packet ({plantItems.length})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={layer === "ops"}
          className={tabBtn(layer === "ops")}
          onClick={() => setLayer("ops")}
        >
          Ops paperwork ({opsItems.length})
        </button>
      </div>
      <p className="mb-2 text-[11px] text-gray-500">
        {layer === "plant"
          ? "What this manufacturer needs from the dealer. Required uploads must be received before Submit to build. Floor plan: upload a file here if the host did not send one."
          : "Ops packet for this deal (contract or cash receipt, waivers)."}
      </p>
      {shown.length === 0 ? (
        <p className="text-sm text-gray-500">
          {layer === "plant" ? "No plant packet slots on this deal." : "No ops paperwork items on this deal."}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          {shown.map((row) => (
            <PacketSlotCard
              key={row.id}
              row={row}
              kind={layer}
              attachments={props.attachmentsByItem.get(row.id) ?? []}
              dealId={props.dealId}
              canEditAttachments={props.canEditAttachments}
              onSave={props.onSavePaperRow}
              onReload={props.onReloadDetail}
            />
          ))}
        </div>
      )}
    </section>
  );
}
